import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterModule,
} from '@angular/router';
import { AsyncPipe, CommonModule, NgFor, NgForOf, NgIf } from '@angular/common';
import {
  toSentenceCase,
  getActivatedRoute,
  getRoutePathFromRoot,
} from '@domgen/dgx-fe-common';
import { Breadcrumb } from '@domgen/dgx-fe-components/navigation';
import { BreadcrumbTranslationDictionary } from '@domgen/dgx-fe-components/navigation/lib/components/breadcrumbs/i18n/breadcrumbs-translation-dictionary.interface';
import {
  LocaleId,
  provideTranslationDictionaryMapping,
  TranslateService,
  TranslationDictionaryMapping,
} from '@domgen/dgx-fe-i18n';
import { Observable, filter, map, startWith } from 'rxjs';
import {
  GridColumnComponent,
  GridContainerComponent,
  GridRowComponent,
} from '@domgen/dgx-fe-components/grid';
import { IconComponent } from '@domgen/dgx-fe-components/icon';

export const breadcrumbsTranslationDictionaryMapping: TranslationDictionaryMapping<BreadcrumbTranslationDictionary> =
  {
    [LocaleId.English_GreatBritain]: {
      BREADCRUMBS_DEFAULT_ROOT_LABEL: 'Home',
    },
    [LocaleId.English_Ireland]: { BREADCRUMBS_DEFAULT_ROOT_LABEL: 'Home' },
    [LocaleId.English_US]: { BREADCRUMBS_DEFAULT_ROOT_LABEL: 'Home' },
    [LocaleId.Italian_Italy]: { BREADCRUMBS_DEFAULT_ROOT_LABEL: 'Home' },
  };

@Component({
  selector: 'dg-smart-fix-hub-breadcrumbs',
  templateUrl: './smart-fix-hub-breadcrumbs.component.html',
  styleUrls: ['./smart-fix-hub-breadcrumbs.component.scss'],
  providers: [
    provideTranslationDictionaryMapping(
      breadcrumbsTranslationDictionaryMapping
    ),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    NgForOf,
    NgIf,
    AsyncPipe,
    GridColumnComponent,
    GridContainerComponent,
    GridRowComponent,
    IconComponent,
    RouterLink,
  ],
})
export class SmartFixHubBreadcrumbsComponent {
  @Input() rootLabel = this.translateService.translate(
    'BREADCRUMBS_DEFAULT_ROOT_LABEL'
  );

  breadcrumbs$: Observable<Breadcrumb[]> = this.buildBreadcrumbs();

  constructor(
    private readonly router: Router,
    private readonly translateService: TranslateService<BreadcrumbTranslationDictionary>
  ) {}

  private buildBreadcrumbs(): Observable<Breadcrumb[]> {
    return this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.activatedRoutePathFromRoot),
      startWith(this.activatedRoutePathFromRoot),
      map((routeSegments: string[]) => this.formatBreadcrumbs(routeSegments))
    );
  }

  private formatBreadcrumbs(routeSegments: string[]): Breadcrumb[] {
    return routeSegments.map((routeSegment: string) => {
      const labelPath: string | undefined = routeSegment.split('/').pop();
      const label = labelPath
        ? toSentenceCase(labelPath.replace(/-/g, ' '))
        : this.rootLabel;

      return {
        label,
        routerPath: routeSegment,
      };
    });
  }

  private get activatedRoutePathFromRoot(): string[] {
    const activatedRoute = getActivatedRoute(this.router.routerState);
    return getRoutePathFromRoot(activatedRoute.snapshot);
  }
}
